<template>
  <div
    class="vue-notification-box"
    ref="container"
    :style="style"
    @mouseenter="cancelHide()"
    @mouseleave="hide()"
  >
    <slot></slot>
  </div>
</template>

<script lang="js">
import EVENT_BUS, { EVENT_BUS_TYPES } from "__event-bus__";

const defaultOffset = () => ({ top: 0, right: 0, bottom: 0, left: 0 });

/**
 * @type {Map<string, any>}
 */
const openedBoxes = new Map();

const hideAllBoxes = () => openedBoxes.forEach(item => item.hide(true));

export default {
  name: "vue-notification-box",

  props: {
    timeout: { type: Number, required: false, default: 350 },
    positioning: { type: String, required: false, default: "absolute" },
    position: { type: String, required: false, default: "bottom right" },
    offset: { type: Object, required: false, default: () => defaultOffset() }
  },

  data() {
    return {
      timeoutId: null,
      visible: false,
      style: {
        position: this.positioning,
        height: "auto",
        width: "max-content",
        zIndex: 10000,
        top: "-3000px",
        left: "-3000px"
      }
    };
  },

  watch: {
    visible(nVal, oVal) {

      if ( !nVal ) {
        this.style.top = "-1000px";
        this.style.left = "-1000px";
        openedBoxes.delete(this.$attrs["data-id"]);
      } else {
        openedBoxes.set(this.$attrs["data-id"], this);
      }
    }
  },

  created() {
    EVENT_BUS.$on(EVENT_BUS_TYPES.NOTIFICATION_BOX_SHOW, this.showHandler);
    EVENT_BUS.$on(EVENT_BUS_TYPES.NOTIFICATION_BOX_HIDE, this.hideHandler);
    EVENT_BUS.$on(EVENT_BUS_TYPES.NOTIFICATION_BOX_TOGGLE, this.toggleHandler);

    this.$on("hook:beforeDestroy", () => {
      EVENT_BUS.$off(EVENT_BUS_TYPES.NOTIFICATION_BOX_SHOW, this.showHandler);
      EVENT_BUS.$off(EVENT_BUS_TYPES.NOTIFICATION_BOX_HIDE, this.hideHandler);
      EVENT_BUS.$off(EVENT_BUS_TYPES.NOTIFICATION_BOX_TOGGLE, this.toggleHandler);
    });
  },

  methods: {
    toggleHandler({ event, options }) {
      if ( options.id !== this.$attrs["data-id"] ) {
        return;
      }

      if ( this.visible ) {
        this.hideHandler({ event, id: options.id });
      } else {
        this.showHandler({ event, options });
      }
    },

    showHandler({ event, options }) {
      if ( options.id !== this.$attrs["data-id"] ) return;
      this.show(event, options);
    },

    hideHandler({ event, id }) {
      if ( id !== this.$attrs["data-id"] ) {
        return;
      }

      this.hide();
    },

    show(event, options) {
      this.cancelHide();
      hideAllBoxes();

      let calcPos = { x: 0, y: 0 };
      const holderEl = options.target ?? event.target.parentElement;
      const contentEl = this.$el.querySelector(".vue-notification-box__content");
      const offset = { ...defaultOffset(), ...this.offset };

      contentEl.classList.remove("arrow--right", "arrow--left", "arrow--top", "arrow--bottom");

      if ( this.position.indexOf("bottom") !== -1 ) {
        const rectHolder = holderEl.getBoundingClientRect();
        const rectContent = contentEl.getBoundingClientRect();

        calcPos.y = rectHolder.y + rectHolder.height + 14;
        calcPos.x = rectHolder.x - (rectContent.width / 2) + (rectHolder.width / 2);

        contentEl.classList.add("arrow--top");
      }

      if ( this.position.indexOf("top") !== -1 ) calcPos.y = offset.top;
      if ( this.position.indexOf("right") !== -1 ) calcPos.x = holderEl.clientWidth;
      if ( this.position.indexOf("left") !== -1 ) calcPos.x = 0;
      if ( this.position.indexOf("auto") !== -1 ) {
        calcPos = this.calcAutoPosition(event, options);
      }

      this.style.top = `${ calcPos.y + offset.top + offset.bottom }px`;
      this.style.left = `${ calcPos.x + offset.left + offset.right }px`;
      this.visible = true;
    },

    calcAutoPosition(event, options) {
      const holderEl = options.target ?? event.target.parentElement;
      const contentEl = this.$el.querySelector(".vue-notification-box__content");
      const contentElClientRect = contentEl.getBoundingClientRect();
      const holderElClientRect = holderEl.getBoundingClientRect();
      const arrowElClientRect = window.getComputedStyle(contentEl, ":before");
      const arrowElWidth = parseInt(arrowElClientRect.width);
      const arrowElHeight = parseInt(arrowElClientRect.height);
      const viewportWidth = window.document.body.clientWidth;
      const position = { x: 0, y: 0 };
      let addedClass = "";

      if (
        viewportWidth -
        (holderElClientRect.right + contentElClientRect.width + arrowElWidth) >= 0
      ) {
        addedClass = "arrow--left";
        position.x = holderElClientRect.width + arrowElWidth;
        position.y = -(holderElClientRect.height / 2);
      } else if (
        holderElClientRect.left - contentElClientRect.width - arrowElWidth >= 0
      ) {
        addedClass = "arrow--right";
        position.x = -(arrowElWidth + contentElClientRect.width);
        position.y = -(holderElClientRect.height / 2);
      } else if (
        holderElClientRect.top - contentElClientRect.height - arrowElHeight >= 0
      ) {
        addedClass = "arrow--bottom";
        position.x = -Math.round(contentElClientRect.width / 2) + Math.round(holderElClientRect.width / 2);
        position.y = -(arrowElHeight + contentElClientRect.height);
      } else {
        addedClass = "arrow--top";
        position.x = -Math.round((contentElClientRect.width + arrowElWidth) / 2);
        position.y = holderElClientRect.bottom + arrowElHeight;
      }

      addedClass && contentEl.classList.add(addedClass);

      return position;
    },

    cancelHide() {
      this.timeoutId && window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    },

    hide(force = (this.timeout === 0)) {
      if ( force ) {
        this.cancelHide();
        this.visible = false;
      } else {
        this.timeoutId = window.setTimeout(() => {
          this.visible = false;
          this.cancelHide();
        }, this.timeout);
      }
    }
  }
};
</script>
